import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import {
    type CommonQueryArgs,
    DAAS_TOKEN_METADATA_CACHE,
    getAptosTokenMetadata,
    getQueryString,
    useQueryDisabled,
} from '@pkg/hooks';

import {
    type AptosTokenData,
    CollectionChainParam,
    SupportedTokens,
    type TokenV2Metadata,
    type UnifiedTokenIdFormat,
} from '@app/client';
import { callGetTokenMetadata } from '@app/client/resources/daas';

interface GetTokenMetadataProps extends CommonQueryArgs {
    tokenIds?: string[];
    unifiedTokenIds?: UnifiedTokenIdFormat[];
    includeSlug?: boolean;
    contract?: string;
    chain?: SupportedTokens;
}
export function useGetTokenMetadata({
    tokenIds,
    unifiedTokenIds,
    chain,
    contract,
    disabled,
    includeSlug = true,
}: GetTokenMetadataProps) {
    const { contractParam, idParam, chainParam } = useParams();

    const _contract = contract || contractParam;
    const _chain =
        chain || (chainParam?.toLowerCase() && SupportedTokens[CollectionChainParam[chainParam?.toLowerCase()]]);
    const _tokenId = tokenIds ? tokenIds?.[0] : idParam;

    let tokenIdQuery =
        _chain === SupportedTokens.Aptos ? [`${_chain}.${_tokenId}`] : [`${_chain}.${_contract}.${_tokenId}`];
    if (tokenIds && tokenIds?.length > 1) {
        tokenIdQuery = tokenIds.map((t) => `${_chain}.${_contract}.${t}`);
    }

    const customQueryString = getQueryString({
        queryArrayKey: 'token_ids',
        queryArray: unifiedTokenIds || tokenIdQuery,
        queryParams: { include_slug: includeSlug },
    });

    const queryDisabled = useQueryDisabled([!disabled, customQueryString?.length]);

    const cache = [DAAS_TOKEN_METADATA_CACHE, customQueryString];
    return useQuery({
        queryKey: cache,
        queryFn: ({ signal }) =>
            callGetTokenMetadata({
                settings: { signal },
                customQuery: customQueryString,
            }),
        retry: false,
        placeholderData: keepPreviousData,
        enabled: !queryDisabled,
        select: (res) => {
            if (res.ok && res.value) {
                if (_chain) {
                    const chainRes = res.value[_chain as SupportedTokens];
                    const data = chainRes?.[0];
                    if (_chain === SupportedTokens.Aptos && data) {
                        return [getAptosTokenMetadata(data as AptosTokenData)];
                    }
                    if (data) {
                        return [{ ...data, image_url: data?.image_url?.replace(',q_fallback', '') }];
                    }
                }
                // TODO - clean up this hook regarding single items/list
                return Object.keys(res.value).flatMap((chain) => {
                    return res.value[chain].map((item) => {
                        if (chain === SupportedTokens.Aptos && item) {
                            return getAptosTokenMetadata(item as AptosTokenData);
                        }
                        return { ...item, image_url: item?.image_url?.replace(',q_fallback', ''), chain };
                    }) as TokenV2Metadata[];
                });
            }
        },
    });
}
